import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Plus, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PageHeader } from '@/components/PageHeader';
import { DataTable } from '@/components/DataTable';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/lib/auth-context';
import { FormDrawer } from '@/components/ui/form-drawer';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { api } from '@/lib/api';

const contactFormSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  title: z.string().optional(),
  email: z.string().email('Invalid email').optional().or(z.literal('')),
  phone: z.string().optional(),
  accountId: z.string().min(1, 'Account is required'),
});

type ContactFormData = z.infer<typeof contactFormSchema>;

interface Contact {
  id: string;
  first_name: string;
  last_name: string;
  title: string | null;
  status: string;
  created_at: string;
  accounts?: Array<{
    accountId: string;
    accountName: string;
    jobTitle: string | null;
    isPrimary: number;
  }>;
}

interface Account {
  id: string;
  name: string;
}

interface ContactsResponse {
  contacts: Contact[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export default function ContactsPage() {
  const { sessionId } = useAuth();
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);

  const { data, isLoading, refetch } = useQuery<ContactsResponse>({
    queryKey: ['/api/contacts', { page: currentPage, limit: pageSize, search }, sessionId],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: pageSize.toString(),
        ...(search && { search }),
      });
      return await api.get<ContactsResponse>(`/contacts?${params}`, sessionId);
    },
    enabled: !!sessionId,
  });

  const { data: accountsData } = useQuery<{ accounts: Account[] }>({
    queryKey: ['/api/accounts', sessionId],
    queryFn: async () => {
      return await api.get('/accounts?limit=1000', sessionId);
    },
    enabled: !!sessionId && dialogOpen,
  });

  const form = useForm<ContactFormData>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      title: '',
      email: '',
      phone: '',
      accountId: '',
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: ContactFormData) => {
      return await api.post('/contacts', data, sessionId);
    },
    onSuccess: () => {
      refetch();
      toast({
        title: 'Contact created',
        description: 'The contact has been created successfully.',
      });
      setDialogOpen(false);
      form.reset();
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to create contact',
        variant: 'destructive',
      });
    },
  });

  const onSubmit = (data: ContactFormData) => {
    createMutation.mutate(data);
  };

  const columns = [
    {
      key: 'name',
      header: 'Name',
      cell: (row: Contact) => (
        <div className="font-medium">
          {row.first_name} {row.last_name}
        </div>
      ),
    },
    {
      key: 'title',
      header: 'Title',
      cell: (row: Contact) => (
        <span className="text-muted-foreground">
          {row.accounts?.[0]?.jobTitle || row.title || '-'}
        </span>
      ),
    },
    {
      key: 'account',
      header: 'Account',
      cell: (row: Contact) => {
        const accounts = row.accounts || [];
        if (accounts.length === 0) return <span className="text-muted-foreground">-</span>;
        return (
          <div className="flex items-center gap-2">
            <span>{accounts[0].accountName}</span>
            {accounts[0].isPrimary === 1 && (
              <Badge variant="secondary" className="text-xs">Primary</Badge>
            )}
            {accounts.length > 1 && (
              <Badge variant="outline" className="text-xs">+{accounts.length - 1}</Badge>
            )}
          </div>
        );
      },
    },
    {
      key: 'status',
      header: 'Status',
      cell: (row: Contact) => (
        <Badge variant={row.status === 'ACTIVE' ? 'default' : 'secondary'}>
          {row.status}
        </Badge>
      ),
    },
  ];

  const contacts = data?.contacts || [];
  const total = data?.total || 0;

  return (
    <div className="space-y-4">
      <PageHeader
        title="Contacts"
        actions={
          <Button size="sm" onClick={() => setDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-1" />
            New Contact
          </Button>
        }
      />

      <div className="flex items-center gap-2">
        <Input
          placeholder="Search contacts..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-sm"
        />
      </div>

      <DataTable
        data={contacts}
        columns={columns}
        isLoading={isLoading}
        onRowClick={(row) => setLocation(`/contacts/${row.id}`)}
        emptyMessage="No contacts found"
        emptyIcon={<Users className="h-12 w-12 text-muted-foreground/50" />}
        pageSize={pageSize}
        totalCount={total}
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
      />

      <FormDrawer
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        title="New Contact"
        description="Add a new contact to your organization."
        formId="create-contact"
        isPending={createMutation.isPending}
        submitLabel="Create Contact"
        size="md"
        data-testid="drawer-new-contact"
      >
        <Form {...form}>
          <form
            id="create-contact"
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-6"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="firstName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>First Name *</FormLabel>
                    <FormControl>
                      <Input variant="flat" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="lastName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Last Name *</FormLabel>
                    <FormControl>
                      <Input variant="flat" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="accountId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Account *</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger variant="flat">
                        <SelectValue placeholder="Select account" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {accountsData?.accounts.map((account) => (
                        <SelectItem key={account.id} value={account.id}>
                          {account.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Job Title</FormLabel>
                  <FormControl>
                    <Input variant="flat" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input variant="flat" type="email" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Phone</FormLabel>
                  <FormControl>
                    <Input variant="flat" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </form>
        </Form>
      </FormDrawer>
    </div>
  );
}
